import { Brain, FileCheck2, Microscope, Users2, ClipboardList } from "lucide-react";
import { Button } from "./ui/button";
import { CONDITIONS } from "../data/content";

const AV_IMG =
  "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=900&q=80";

const STEPS = [
  {
    icon: Users2,
    title: "Anamnese",
    desc: "Primeira sessão com o paciente e um familiar/responsável que conheça bem sua rotina.",
  },
  {
    icon: ClipboardList,
    title: "Aplicação dos testes",
    desc: "Baterias padronizadas para a população brasileira que avaliam atenção, memória, praxias, linguagem, funções executivas, QI e humor.",
  },
  {
    icon: Microscope,
    title: "Análise técnica",
    desc: "Interpretação clínica dos resultados com correlação entre funções preservadas e comprometidas.",
  },
  {
    icon: FileCheck2,
    title: "Laudo completo",
    desc: "Relatório detalhado que orienta diagnóstico, conduta médica, escolar ou terapêutica.",
  },
];

export const AvaliacaoNeuro = () => {
  return (
    <section
      id="avaliacao"
      data-testid="neuro-section"
      className="relative py-20 md:py-28 bg-royal-900 text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <div className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-gold-500 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.2em] uppercase text-gold-500 font-semibold flex items-center gap-2">
            <Brain size={14} /> Serviço especializado
          </div>
          <h2 className="mt-3 font-serif-display text-3xl md:text-5xl leading-tight">
            Avaliação <br /> Neuropsicológica
          </h2>
          <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
            Ferramenta precisa e atual usada por médicos para diagnosticar com exatidão e
            orientar condutas. A avaliação é composta por vários testes importados e
            padronizados que aferem funções intelectuais e executivas.
          </p>
          <p className="mt-4 text-white/70 text-sm leading-relaxed">
            Indicada para crianças, adolescentes, adultos e idosos — em suspeita de TDAH,
            Alzheimer, AVC, TEA, dislexia, depressão, ansiedade, traumatismos e outros quadros
            que envolvem o funcionamento cognitivo.
          </p>
          <div className="mt-8 relative">
            <div className="absolute -inset-3 bg-gold-500/20 rounded-2xl rotate-1" />
            <img
              src={AV_IMG}
              alt="Cérebro e neurociência - Avaliação Neuropsicológica"
              data-testid="neuro-image"
              className="relative w-full aspect-[5/4] object-cover rounded-2xl border border-white/10"
            />
          </div>
          <Button
            asChild
            data-testid="neuro-cta"
            className="mt-8 bg-gold-500 hover:bg-gold-600 text-royal-900 font-semibold h-12 px-6 rounded-md"
          >
            <a href="#contato">Solicitar avaliação</a>
          </Button>
        </div>

        <div className="lg:col-span-7">
          <ol className="space-y-4">
            {STEPS.map((s, i) => {
              const Ico = s.icon;
              return (
                <li
                  key={i}
                  data-testid={`neuro-step-${i}`}
                  className="p-6 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex gap-5"
                >
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-gold-500/15 text-gold-500 flex items-center justify-center">
                    <Ico size={22} />
                  </div>
                  <div>
                    <div className="text-xs tracking-widest uppercase text-gold-500/80">
                      Etapa {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-1 font-serif-display text-2xl">{s.title}</h3>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.desc}</p>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="mt-10 p-6 rounded-xl border border-white/10 bg-white/5">
            <p className="text-xs tracking-[0.2em] uppercase text-gold-500 font-semibold">
              Condições atendidas no consultório
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <span
                  key={c}
                  data-testid={`condition-chip-${c}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-white/80"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
